import DPO from 'dpo8core';
import { getWhiteTreatmentHint, getOriginalNameFromHint, getWhiteSpotColorDetected } from '@app/utils/hints';
import { CORNER_TYPES } from '@app/constants';

export const getFileExt = ({ name }) => {
  const [, fileExt = ''] = name.match(/\.([0-9a-z]+)$/i);
  return fileExt.toLowerCase();
};

export const getFileName = ({ name }) => {
  const [, fileName = ''] = name.match(/(.*)\.[0-9a-z]+$/i);
  return fileName;
};

export const getFileType = ({ type }) => {
  const [, fileType] = type.split('/');
  return fileType;
};

export const roundNum = (value, dec) => Number(Math.round(Number(parseFloat(value) + 'e' + dec)) + 'e-' + dec);

//local storage
export const checkOptionFromLocalStorage = (option, defaultValue) => {
  const val = localStorage.getItem(option);
  const notInStorage = val === null && defaultValue !== undefined;
  if (notInStorage) return defaultValue;
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
};

export const setOptionToLocaleStorage = (option, value) => {
  localStorage.setItem(option, JSON.stringify(value));
};

export const isRollFlow = ({ features }) => !!features?.featureExistsAndEnabled('rollFlow');

export const isShortWePrintFlow = ({ features }) => !!features?.featureExistsAndEnabled('shortWePrintFlow');

export const isStickerFlow = ({ features }) => !!features?.featureExistsAndEnabled('stickerFlow');

export const isSheetFlow = ({ features }) => !!features?.featureExistsAndEnabled('sheetFlow');

export const isCatalogSheetFlow = ({ features }) =>
  !!features?.featureExistsAndEnabled('sheetFlow') && !features?.featureExistsAndEnabled('customSizeSheetFlow');

export const isSkipNameStep = ({ features }) => !!features?.featureExistsAndEnabled('skipQAUNameStep');

export const isSaveUploadArtworkToMyAccount = ({ features }) => !!features?.featureExistsAndEnabled('saveUploadArtworkToMyAccount');

export const isDesignIn = ({ features }) => isRollFlow({ features }) && !!features?.featureExistsAndEnabled('designIn');

export const isCustomShapeSticker = ({ features }) => !!features?.featureExistsAndEnabled('customShapeStickerFlow');

export const isCustomShapeSheet = ({ features }) => !!features?.featureExistsAndEnabled('customShapeSheetFlow');

export const isCustomShapeFlow = fc => isCustomShapeSheet(fc) || isCustomShapeSticker(fc);

export const isImageRemoveWhite = ({ features }) => !!features?.featureExistsAndEnabled('imageRemoveWhite');

export const isAddWhiteColor = quote => quote?.addWhiteColor === 'Y';

export const isRoll = flowConfig =>
  (isRollFlow(flowConfig) || isShortWePrintFlow(flowConfig)) && !isDesignIn(flowConfig) && !isStickerFlow(flowConfig);

export const isSticker = flowConfig => isStickerFlow(flowConfig) && !isCustomShapeSticker(flowConfig);

export const isIntLabelProduct = flowConfig => isShortWePrintFlow(flowConfig) && isSkipNameStep(flowConfig);

export const isApplyCodesViewAvailable = ({ features }) =>
  !!features?.featureExistsAndEnabled('applyCodesView') && isDesignIn({ features });

export const isDisableShapeChange = ({ features }) => !!features?.featureExistsAndEnabled('disableShapeChange');

export const isArtworkInfoEnabled = ({ features }) => !!features?.featureExistsAndEnabled('artworkInfo');

export const isEditingArtworkWarningsEnabled = ({ features }) => !!features?.featureExistsAndEnabled('editingArtworkWarnings');

export const isCornerTypeOptionsEnabled = ({ features }) => !!features?.featureExistsAndEnabled('cornerTypeOptions');

export const parseError = (errors, codes) => {
  if (!errors || !codes) return false;
  codes = Array.isArray(codes) ? codes : [codes];
  for (let i = 0; i < errors.length; i++) {
    const error = errors[i];
    const code = parseInt(error.errorCode || error.code);
    const message = error.errorMessage || error.message;
    if (~codes.indexOf(code)) return { code, message };
  }
  return false;
};

export const loadProject = (artworkInfo, flow, context, matrix) => {
  const { artwork_status: artworkStatus } = artworkInfo;
  const { projectData } = context;
  const {
    customize: { storage, path },
  } = matrix;

  const { projectLocation: location = {} } = projectData || {};

  const canLoadProjectByPath =
    !flow.projectData || location.storageId !== storage || location.relativePath !== path || artworkStatus === 'WITH_DIELINE';

  canLoadProjectByPath && flow.loadProjectByPath(storage, path);
};

export const isJsonStr = str => {
  let parsedStr = str;

  try {
    parsedStr = JSON.parse(str);
  } catch {
    return false;
  }

  return typeof parsedStr === 'object';
};

export const generateRandomKey = () => {
  const randomString =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15) +
    Date.now().toString(36);

  return randomString.substr(-32);
};

export const flattened = array => array.reduce((arr, item) => arr.concat(item), []);

export const smoothScrolling = e => {
  e.preventDefault();
  const href = e.target.getAttribute('href');
  document.querySelector(href).scrollIntoView({
    behavior: 'smooth',
  });
};

export const isPolygonShape = shape => !['rect', 'square', 'ellipse', 'circle'].includes(shape);

export const isCustomShape = shape => shape === 'custom';

export const isMobileMode = ({ mode }) => !!mode && mode.toLowerCase() === 'mobile';

export const isTabletMode = ({ mode }) => !!mode && mode.toLowerCase() === 'tablet';

export const getDOMElRect = id => {
  const el = document.getElementById(id);
  return el ? el.getBoundingClientRect() : { width: 0, height: 0 };
};

export const checkShapeIsEquilateral = shape =>
  ['scallop_square', 'scallop_circle', 'starburst_circle', 'star', 'heart'].includes(shape);

export const zoomToPercent = zoomIndex => (zoomIndex * 100).toFixed();

export const zoomToIndex = zoomPercent => zoomPercent / 100;

export const areArraysEqual = (firstArray, secondArray) => {
  if (firstArray?.length !== secondArray?.length) return false;

  const arraysLength = firstArray.length;

  for (let i = 0; i < arraysLength; i++) {
    // check if there are nested arrays
    if (Array.isArray(firstArray[i]) && Array.isArray(secondArray[i])) {
      // recurse into the nested arrays
      if (!areArraysEqual(firstArray[i], secondArray[i])) return false;
    } else if (firstArray[i] !== secondArray[i]) {
      return false;
    }
  }
  return true;
};

export const isImageFitDisabled = (flow = {}) => {
  const { editor: { selectedFields: [{ rotation } = {}] = [] } = {} } = flow;
  return ![0, 90, 180, 270].includes(rotation);
};

export const getOriginalSourceValue = (flow, image) => {
  if (!image) {
    const {
      editor: {
        masterPanel: { images },
      },
    } = flow;
    image = images.find(i => i.visible);
    if (!image) return;
  }

  return getWhiteTreatmentHint(image.hints)?.value !== 'keep' ? getOriginalNameFromHint(image.hints)?.value : '';
};

export const findRmbgImg = ({
  editor: {
    masterPanel: { images },
  },
}) => {
  for (const img of images) {
    const hint = img.getHintValue('removedBackgroundImg');
    if (hint) {
      return {
        removed: images.find(i => i.source === hint || i.getHintValue('original') === hint),
        keep: img,
      };
    }
  }
  if (images.length) {
    const [keep] = images;
    return { keep };
  }
  return {};
};

export const checkForWhiteIfNeeded = (flowConfig, context, image, isMobileView, setWhiteInkOptionsNeeded) => {
  const imageAnalyzer = new DPO.project.ImageAnalyzer(context, flowConfig);
  const { hints, source: imageName } = image;
  const whiteTreatmentHint = getWhiteTreatmentHint(hints);
  const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');
  const isRemoveWhiteFeatureEnabled = removeWhiteFeature && !removeWhiteFeature.disabled;
  const imageSrc = context.storage.lowImgMap[imageName]?.uri;
  const isAddWhiteColorEnabled = context.isAddWhiteColorEnabled();
  const isWhiteSpotColorDetected = getWhiteSpotColorDetected(context);
  const checkForWhiteNeeded =
    !isMobileView && context.paperMaterial && isAddWhiteColorEnabled && isRemoveWhiteFeatureEnabled && !isWhiteSpotColorDetected;

  const onImageAnalyzed = results => {
    const whiteInkOptionsNeeded = results.isEnoughWhite && !results.isWhiteOnly && !results.exceedsTransparencyLimit;

    setWhiteInkOptionsNeeded(whiteInkOptionsNeeded || !!whiteTreatmentHint);
  };

  if (checkForWhiteNeeded) {
    whiteTreatmentHint
      ? setWhiteInkOptionsNeeded(true)
      : imageAnalyzer.analyzeBySrc(
          {
            imageSrc: imageSrc,
            tolerance: removeWhiteFeature.getParamValue('maxTolerance'),
          },
          onImageAnalyzed,
        );
  } else {
    setWhiteInkOptionsNeeded(false);
  }
};

export const isValidArtwork = ({ artwork_status }, fc = {}) => {
  const feature = fc?.features?.getFeature('validArtworkStatus');
  if (!feature) return false;

  const validStatuses = feature.getAllParamValues();
  return validStatuses.includes(artwork_status);
};

export const convertToFlatShapeList = ({ customShapes = [], standardShapes = [], polygonShapes = [], scallopShapes = [] } = {}) => [
  ...customShapes,
  ...standardShapes,
  ...polygonShapes,
  ...scallopShapes,
];

export const getShapeByCornerType = (shape, cornerType, { scallopShapes }) => {
  let scallopedShape;
  switch (cornerType) {
    case CORNER_TYPES.ROUNDED_CORNER:
    case CORNER_TYPES.SQUARED_CORNER:
    case CORNER_TYPES.SMOOTH_EDGE:
      return shape && shape.replace('scallop_', '');
    case CORNER_TYPES.SCALLOPED_CORNER:
    case CORNER_TYPES.SCALLOPED_EDGE:
      scallopedShape = shape && `scallop_${shape.replace('scallop_', '')}`;
      return scallopShapes?.find(({ shape }) => shape === scallopedShape) ? scallopedShape : shape;
    default:
      return shape;
  }
};

export const getDefaultCornerRadius = (width, height, cornerType, { project: { panelDefaultCornerRadius } }) => {
  return panelDefaultCornerRadius.getSize({ width, height })[cornerType];
};

export const getPanelFields = (panel, selector) => {
  if (panel?.groups.length) {
    const [group] = panel.groups;
    return selector.selectGroupFields(group);
  }
  const { images = [] } = selector.selectMasterPanel(panel) || {};
  let field = images.find(f => f.visible);

  if (field) {
    field.movable = true;
    return [field];
  }
  return [];
};

export const collectAutoSaveImages = (flow, selector, artworkData) => {
  const { keep: { source, hints } } = findRmbgImg(flow);
  flow.autoSaveImages = [{
      name: getOriginalNameFromHint(hints)?.value || source,
      originalName: artworkData.artwork_filename,
      source: 'complete-design',
      override: true,
    },];
};

export const parseSignUpError = errors  => {
  if (!errors) return 'Invalid data';
  try {
    errors = JSON.parse(errors);
  } finally {
    const [ error ] = Array.isArray(errors) ? errors : [ errors ];
    return error.message || error;
  }
};